import React from 'react';
import { useAsset } from '../hooks/useAsset';
import { GameMode } from '../types';
import { Language, t } from '../data/translations';
import ShellSlot from './shell/ShellSlot';
import { useShellLayout } from '../hooks/useShellLayout';
import { useMobileAutoScrollHint } from '../hooks/useMobileAutoScrollHint';

interface MainMenuProps {
  onNavigate: (mode: GameMode) => void;
  onQuit: () => void;
  language: Language;
}

const TitleSparks = ({ count = 6 }: { count?: number }) => {
  const [sparks, setSparks] = React.useState<{ id: number; left: string; bottom: string; size: string; delay: string; duration: string }[]>([]);

  React.useEffect(() => {
    const newSparks = Array.from({ length: count }).map((_, i) => ({
      id: i,
      left: `${Math.random() * 90 + 5}%`,
      bottom: `${Math.random() * 80 + 10}%`,
      size: `${Math.random() * 6 + 4}px`,
      delay: `${Math.random() * 4}s`,
      duration: `${Math.random() * 4 + 5}s`,
    }));
    setSparks(newSparks);
  }, [count]);

  return (
    <>
      {sparks.map((spark) => (
        <div
          key={spark.id}
          className="magic-spark"
          style={{
            left: spark.left,
            bottom: spark.bottom,
            width: spark.size,
            height: spark.size,
            animationDelay: spark.delay,
            animationDuration: spark.duration,
          }}
        />
      ))}
    </>
  );
};

const MainMenu: React.FC<MainMenuProps> = ({ onNavigate, onQuit, language }) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const isAdmin = localStorage.getItem('oko_is_admin') === 'true';
  const { isMobileShell } = useShellLayout();
  const menuBg = useAsset('ui.menu.bg');
  const btnPrimary = useAsset('ui.menu.btnPrimary');
  useMobileAutoScrollHint({ targetRef: rootRef, sceneKey: 'main-menu' });
  const titleLogoClass = isMobileShell ? 'text-4xl' : 'text-6xl sm:text-8xl';
  const subtitleLogoClass = isMobileShell ? 'text-2xl mt-[-2px]' : 'text-4xl sm:text-5xl mt-[-7px]';
  const onlineLogoClass = isMobileShell ? 'text-base mt-2' : 'text-2xl sm:text-3xl mt-4';
  const menuButtonClass = isMobileShell
    ? 'min-h-[46px] text-base py-2.5 px-5'
    : 'min-h-[54px] text-lg py-3 px-6';

  return (
    <div
      className="relative w-full overflow-hidden text-white animate-[fadeIn_0.5s_ease-out]"
      style={{
        minHeight: 'var(--shell-viewport-height, 100vh)',
        backgroundImage: `url(${menuBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {isMobileShell && <div className="absolute inset-0 bg-slate-950/10" />}
      <ShellSlot slot="menu-content">
        <div ref={rootRef} className={`relative z-10 flex min-h-full w-full flex-col items-center justify-center ${isMobileShell ? 'max-w-[380px]' : 'max-w-[540px]'}`}>
          <div className={`relative z-10 flex flex-col items-center select-none pointer-events-none ${isMobileShell ? 'mb-5 mt-0' : 'mb-6 mt-[-30px]'}`}>
            <TitleSparks count={20} />

            <h1 className={`${titleLogoClass} text-center text-opalstone-logo`}>
              Opalstone
            </h1>

            <h1 className={`${subtitleLogoClass} text-center text-kingdom-logo`}>
              Kingdom
            </h1>

            <h2 className={`${onlineLogoClass} text-center text-online-logo`}>
              ~ ONLINE ~
            </h2>
          </div>

          <div className={`flex w-full flex-col ${isMobileShell ? 'max-w-full gap-3' : 'mt-6 max-w-64 gap-4'}`}>
            <button
              onClick={() => onNavigate('GAME_MODE_SELECT')}
              className={`${menuButtonClass} text-white font-bold rounded-lg transition-all duration-300 shadow-lg transform hover:scale-105`}
              style={{ backgroundImage: `url(${btnPrimary})`, backgroundSize: '100% 100%', backgroundPosition: 'center' }}
            >
              {t('ui.newGame', language)}
            </button>
            <button
              onClick={() => onNavigate('WORLD_MAP')}
              className={`${menuButtonClass} text-white font-bold rounded-lg transition-all duration-300 shadow-lg transform hover:scale-105`}
              style={{ backgroundImage: `url(${btnPrimary})`, backgroundSize: '100% 100%', backgroundPosition: 'center' }}
            >
              {t('ui.worldMap', language)}
            </button>
            <button
              onClick={() => onNavigate('PROFILE')}
              className={`${menuButtonClass} text-white font-bold rounded-lg transition-all duration-300 shadow-lg transform hover:scale-105`}
              style={{ backgroundImage: `url(${btnPrimary})`, backgroundSize: '100% 100%', backgroundPosition: 'center' }}
            >
              {t('ui.myProfile', language)}
            </button>
            <button
              onClick={() => onNavigate('OPTIONS')}
              className={`${menuButtonClass} text-white font-bold rounded-lg transition-all duration-300 shadow-lg transform hover:scale-105`}
              style={{ backgroundImage: `url(${btnPrimary})`, backgroundSize: '100% 100%', backgroundPosition: 'center' }}
            >
              {t('ui.options', language)}
            </button>
            {isAdmin && (
              <>
                <button
                  onClick={() => onNavigate('PREVIEWER')}
                  className={`${menuButtonClass} text-cyan-400 font-bold rounded-lg transition-all duration-300 shadow-lg transform hover:scale-110 border-2 border-cyan-800/50 bg-cyan-950/40`}
                  style={{ backgroundImage: `url(${btnPrimary})`, backgroundSize: '100% 100%', backgroundPosition: 'center', backgroundBlendMode: 'overlay' }}
                >
                  ✨ Effect Previewer
                </button>

                <button
                  onClick={() => onNavigate('ADMIN_DASHBOARD')}
                  className={`${menuButtonClass} text-yellow-400 font-bold rounded-lg transition-all duration-300 shadow-lg transform hover:scale-110 border-2 border-yellow-600/50 bg-yellow-900/20`}
                  style={{ backgroundImage: `url(${btnPrimary})`, backgroundSize: '100% 100%', backgroundPosition: 'center', backgroundBlendMode: 'overlay' }}
                >
                  {t('ui.adminDashboard', language)}
                </button>
              </>
            )}

            <button
              onClick={onQuit}
              className={`${menuButtonClass} text-white font-bold rounded-lg transition-all duration-300 shadow-lg transform hover:scale-105`}
              style={{ backgroundImage: `url(${btnPrimary})`, backgroundSize: '100% 100%', backgroundPosition: 'center' }}
            >
              {t('ui.quit', language)}
            </button>
          </div>
        </div>
      </ShellSlot>
    </div>
  );
};

export default MainMenu;
